import { ActionIcon, Group, MantineStyleProp, Menu, TextInput, Tooltip } from "@mantine/core";
import { DataTable, type DataTableColumn, type DataTableSortStatus } from "mantine-datatable";
import { IconDownload, IconFileTypeCsv, IconPrinter, IconSearch } from "@tabler/icons-react";
import { useMemo, useState } from "react";

interface ResponsiveTableProps<T = Record<string, any>> {
    data: T[];
    columns: DataTableColumn<T>[];
    gridRowId?: keyof T | ((record: T) => React.Key);
    gridSortModel?: { columnAccessor: keyof T | string; direction: "asc" | "desc" };
    rowSelectionModel?: React.Key[];
    onSelectionChange?: (newSelection: React.Key[]) => void;
    onRowClick?: (row: T) => void;
    selectionColumnStyle?: MantineStyleProp;
}

function downloadCsv<T>(rows: T[], columns: DataTableColumn<T>[], filename = "export.csv") {
    const visibleColumns = columns.filter((col) => col.accessor);

    const header = visibleColumns.map((col) => String(col.title ?? col.accessor));
    const body = rows.map((row) =>
        visibleColumns.map((col) => {
            const value = row[col.accessor as keyof T];
            const text = value == null ? "" : String(value);
            return `"${text.replace(/"/g, '""')}"`;
        })
    );

    const csv = [header.join(","), ...body.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();

    URL.revokeObjectURL(url);
}

function printTable<T>(rows: T[], columns: DataTableColumn<T>[]) {
    const visibleColumns = columns.filter((col) => col.accessor);

    const html = `
        <html>
            <head>
                <title>Print table</title>
                <style>
                    body { font-family: Ubuntu, Arial, sans-serif; padding: 24px; }
                    table { width: 100%; border-collapse: collapse; }
                    th, td { border: 1px solid #ccc; padding: 8px 10px; text-align: left; }
                    th { background: #f5f5f5; }
                </style>
            </head>
            <body>
                <table>
                    <thead>
                        <tr>
                            ${visibleColumns
                                .map((col) => `<th>${String(col.title ?? col.accessor)}</th>`)
                                .join("")}
                        </tr>
                    </thead>
                    <tbody>
                        ${rows
                            .map(
                                (row) => `
                                <tr>
                                    ${visibleColumns
                                        .map((col) => {
                                            const value = row[col.accessor as keyof T];
                                            return `<td>${value == null ? "" : String(value)}</td>`;
                                        })
                                        .join("")}
                                </tr>
                            `
                            )
                            .join("")}
                    </tbody>
                </table>
            </body>
        </html>
    `;

    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
}

function ResponsiveTable<T extends Record<string, any>>({
    data,
    columns,
    gridRowId,
    gridSortModel,
    rowSelectionModel,
    onSelectionChange,
    onRowClick,
    selectionColumnStyle
}: ResponsiveTableProps<T>) {
    const pageSizes = [10, 15, 30];
    const [pageSize, setPageSize] = useState<number>(pageSizes[1]);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [sortStatus, setSortStatus] = useState<DataTableSortStatus<T>>(
        gridSortModel
            ? {
                  columnAccessor: gridSortModel.columnAccessor as string,
                  direction: gridSortModel.direction,
              }
            : {
                  columnAccessor: String(columns[0]?.accessor ?? "id"),
                  direction: "asc",
              }
    );

    const filteredData = useMemo(() => {
        if (!search.trim()) return data;

        const query = search.toLowerCase();

        return data.filter((row) =>
            columns.some((col) => {
                const value = row[col.accessor as keyof T];
                return String(value ?? "")
                    .toLowerCase()
                    .includes(query);
            })
        );
    }, [data, columns, search]);

    const sortedData = useMemo(() => {
        const sorted = [...filteredData];

        sorted.sort((a, b) => {
            const aValue = a[sortStatus.columnAccessor as keyof T];
            const bValue = b[sortStatus.columnAccessor as keyof T];

            if (aValue == null) return 1;
            if (bValue == null) return -1;

            const result = String(aValue).localeCompare(String(bValue), undefined, {
                numeric: true,
                sensitivity: "base",
            });

            return sortStatus.direction === "asc" ? result : -result;
        });

        return sorted;
    }, [filteredData, sortStatus]);

    const paginatedData = useMemo(() => {
        const from = (page - 1) * pageSize;
        const to = from + pageSize;
        return sortedData.slice(from, to);
    }, [sortedData, page, pageSize]);

    return (
        <div>
            <Group justify="space-between" my="sm">
                <TextInput
                    placeholder="Search"
                    value={search}
                    onChange={(event) => {
                        setSearch(event.currentTarget.value);
                        setPage(1);
                    }}
                    leftSection={<IconSearch size="1.125rem" />}
                />

                <Tooltip label="Export" position="bottom">
                    <Menu shadow="md" width={220}>
                        <Menu.Target>
                            <ActionIcon className="unify-action-icon-subtle" aria-label="Export">
                                <IconDownload size="1.125rem" />
                            </ActionIcon>
                        </Menu.Target>

                        <Menu.Dropdown>
                            <Menu.Label>Export</Menu.Label>

                            <Menu.Item
                                leftSection={<IconFileTypeCsv size="1.125rem" />}
                                onClick={() => downloadCsv(sortedData, columns)}
                            >
                                Export as CSV
                            </Menu.Item>

                            <Menu.Item
                                leftSection={<IconPrinter size="1.125rem" />}
                                onClick={() => printTable(sortedData, columns)}
                            >
                                Print
                            </Menu.Item>
                        </Menu.Dropdown>
                    </Menu>
                </Tooltip>
            </Group>

            <DataTable
                withTableBorder={false}
                verticalSpacing="sm"
                borderRadius="md"
                highlightOnHover
                records={paginatedData}
                columns={columns}
                totalRecords={sortedData.length}
                recordsPerPage={pageSize}
                recordsPerPageOptions={pageSizes}
                recordsPerPageLabel=""
                onRecordsPerPageChange={setPageSize}
                page={page}
                onPageChange={setPage}
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                paginationActiveBackgroundColor="var(--primary-color)"
                paginationActiveTextColor="var(--primary-text-color)"
                paginationText={({ from, to, totalRecords }) =>
                    `${from} – ${to} of ${totalRecords}`
                }
                selectedRecords={
                    rowSelectionModel
                        ? data.filter((row) => {
                              const rowId =
                                  typeof gridRowId === "function"
                                      ? gridRowId(row)
                                      : typeof gridRowId === "string"
                                      ? row[gridRowId]
                                      : row.id;

                              return rowSelectionModel.includes(rowId);
                          })
                        : []
                }
                onSelectedRecordsChange={(records) => {
                    if (!onSelectionChange) return;

                    const ids = records.map((row) =>
                        typeof gridRowId === "function"
                            ? gridRowId(row)
                            : typeof gridRowId === "string"
                            ? row[gridRowId]
                            : row.id
                    );

                    onSelectionChange(ids);
                }}
                onRowClick={({ record }) => onRowClick?.(record)}
                idAccessor={
                    gridRowId
                        ? gridRowId
                        : ("id" as keyof T)
                }
                backgroundColor="var(--page-body-color)"
                styles={{
                    pagination: {
                        borderTop: "0",
                    },
                }}
                selectionColumnStyle={selectionColumnStyle}
            />
        </div>
    );
}

export default ResponsiveTable;